<template>
    <div class="fac-chart">
        <div class="fac-chart__header">
            <div>
                <p class="fac-chart__eyebrow">Capacity insight</p>
                <h3 class="fac-chart__title">Seating capacity breakdown</h3>
            </div>
            <div v-if="total > 0" class="fac-chart__total">
                <span class="fac-chart__total-label">Total</span>
                <span class="fac-chart__total-value">{{ total }}</span>
            </div>
        </div>
        <div v-if="!hasData" class="fac-chart__empty">No seating capacity data available yet.</div>
        <apexchart
            v-else
            type="bar"
            height="300"
            :options="chartOptions"
            :series="series"
            @dataPointSelection="onBarSelect"
        />
    </div>
</template>

<script>
export default {
    name: "FacilitySeatingCapacityChart",
    props: {
        data: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    emits: ["select"],
    data() {
        return {
            series: [],
            activeRows: [],
            chartOptions: {
                chart: {
                    type: "bar",
                    fontFamily: "inherit",
                    toolbar: { show: false },
                    animations: { enabled: true, easing: "easeinout", speed: 450 },
                },
                colors: ["#b45309", "#0f766e", "#57534e", "#ca8a04"],
                plotOptions: {
                    bar: {
                        borderRadius: 6,
                        borderRadiusApplication: "end",
                        columnWidth: "48%",
                        distributed: true,
                    },
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        colors: ["#1c1917"],
                    },
                    offsetY: -2,
                },
                legend: { show: false },
                grid: {
                    borderColor: "#f5f5f4",
                    strokeDashArray: 3,
                    padding: { top: 8, bottom: 0 },
                },
                xaxis: {
                    categories: [],
                    labels: {
                        style: {
                            colors: "#57534e",
                            fontSize: "11px",
                            fontWeight: 600,
                        },
                        rotate: 0,
                        trim: true,
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    labels: {
                        style: { colors: "#78716c", fontSize: "11px" },
                        formatter: function (val) {
                            return Math.round(val);
                        },
                    },
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " seats";
                        },
                    },
                },
                states: {
                    hover: {
                        filter: { type: "lighten", value: 0.08 },
                    },
                    active: {
                        allowMultipleDataPointsSelection: false,
                        filter: { type: "darken", value: 0.15 },
                    },
                },
            },
        };
    },
    computed: {
        hasData() {
            return (
                Array.isArray(this.data) &&
                this.data.some(function (item) {
                    return item && item.value > 0;
                })
            );
        },
        total() {
            if (!Array.isArray(this.data)) return 0;
            return this.data.reduce(function (sum, item) {
                return sum + (item && item.value ? item.value : 0);
            }, 0);
        },
    },
    watch: {
        data: {
            handler: function (rows) {
                if (!Array.isArray(rows)) return;
                this.activeRows = rows;
                this.series = [
                    {
                        name: "Seats",
                        data: rows.map(function (item) {
                            return item.value || 0;
                        }),
                    },
                ];
                this.chartOptions = Object.assign({}, this.chartOptions, {
                    xaxis: Object.assign({}, this.chartOptions.xaxis, {
                        categories: rows.map(function (item) {
                            return item.label;
                        }),
                    }),
                });
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        onBarSelect(event, chartContext, config) {
            var index =
                config && typeof config.dataPointIndex === "number"
                    ? config.dataPointIndex
                    : -1;
            if (index < 0 || index >= this.activeRows.length) return;

            var row = this.activeRows[index];
            if (!row || !row.key) return;

            this.$emit("select", {
                key: row.key,
                label: row.label,
                value: row.value,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.fac-chart {
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 1rem;
    padding: 1.25rem 1.35rem 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    height: 100%;
}

.fac-chart__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.35rem;
}

.fac-chart__eyebrow {
    margin: 0;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-chart__title {
    margin: 0.2rem 0 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1c1917;
}

.fac-chart__total {
    text-align: right;
    flex-shrink: 0;
}

.fac-chart__total-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #a8a29e;
}

.fac-chart__total-value {
    display: block;
    margin-top: 0.15rem;
    font-size: 1.25rem;
    font-weight: 800;
    color: #0f766e;
    line-height: 1;
}

.fac-chart__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    color: #a8a29e;
    font-size: 0.9rem;
}

.fac-chart :deep(.apexcharts-bar-area) {
    cursor: pointer;
}
</style>
